/**
 * Vintage pixel-art for the blackjack overlay — a top-down felt poker table and a
 * suited skeleton croupier, drawn programmatically (no binary assets, like
 * `cardArt.ts` / `itemArt.ts`). Both are rendered small and scaled up with
 * `image-rendering: pixelated` so the edges read as chunky pixels.
 */

/** A top-down oval poker table: wood rail, green felt, a betting arc. */
export function drawPokerTable(): HTMLCanvasElement {
  const W = 240;
  const H = 150;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;

  const cx = W / 2;
  const cy = H / 2;
  const oval = (rx: number, ry: number, fill: string) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  };

  oval(cx, cy, "#000000"); // outer shadow rim
  oval(cx - 3, cy - 3, "#5a3a1e"); // wood rail (brown)
  oval(cx - 5, cy - 5, "#7a5230"); // rail highlight
  oval(cx - 13, cy - 13, "#0d4f29"); // felt edge (dark green)
  oval(cx - 16, cy - 16, "#1f8b43"); // felt (casino green)

  // Betting arc + a faint center line (gold).
  ctx.strokeStyle = "#e6cf8a";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(cx, cy + 8, cx - 40, cy - 34, 0, 0, Math.PI * 2);
  ctx.stroke();

  // Wood plank seams on the rail (top + bottom).
  ctx.strokeStyle = "#3a2410";
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 8) {
    const rxOut = cx - 4;
    const ryOut = cy - 4;
    const rxIn = cx - 12;
    const ryIn = cy - 12;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * rxIn, cy + Math.sin(a) * ryIn);
    ctx.lineTo(cx + Math.cos(a) * rxOut, cy + Math.sin(a) * ryOut);
    ctx.stroke();
  }
  return canvas;
}

const BONE = "#e8e8e8";
const BONE_SHADE = "#bdbdbd";
const SUIT = "#151515";
const SHIRT = "#efefef";
const TIE = "#777777";
const SOCKET = "#000000";

/** A front-facing skeleton croupier in a black suit + bow tie. ~48x66, pixelated. */
export function drawSkeletonDealer(): HTMLCanvasElement {
  const W = 48;
  const H = 66;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  const px = (x: number, y: number, w: number, h: number, c: string) => {
    ctx.fillStyle = c;
    ctx.fillRect(x, y, w, h);
  };

  // --- Suit jacket / shoulders (drawn first, behind the head's jaw) ---
  px(8, 36, 32, 24, SUIT); // torso
  px(6, 38, 4, 18, SUIT); // left shoulder
  px(38, 38, 4, 18, SUIT); // right shoulder
  px(20, 36, 8, 24, SHIRT); // shirt placket
  // Lapels.
  px(18, 36, 4, 12, SUIT);
  px(26, 36, 4, 12, SUIT);
  // Bow tie.
  px(20, 35, 8, 4, TIE);
  px(23, 35, 2, 4, "#555555");
  // Skeletal hands resting on the felt.
  px(6, 56, 6, 4, BONE);
  px(36, 56, 6, 4, BONE);

  // --- Skull ---
  px(16, 4, 16, 16, BONE); // cranium
  px(14, 7, 2, 9, BONE);
  px(32, 7, 2, 9, BONE);
  px(17, 3, 14, 1, BONE_SHADE); // top shade
  // Eye sockets.
  px(18, 9, 4, 4, SOCKET);
  px(26, 9, 4, 4, SOCKET);
  px(19, 10, 1, 1, "#999999"); // faint glow
  px(27, 10, 1, 1, "#999999");
  // Nasal cavity.
  px(23, 13, 2, 3, SOCKET);
  // Jaw + teeth.
  px(17, 18, 14, 4, BONE);
  px(18, 20, 12, 1, SUIT);
  for (let x = 18; x < 30; x += 2) px(x, 18, 1, 3, BONE_SHADE);
  // Neck vertebra.
  px(22, 22, 4, 4, BONE);

  // Top hat (a croupier flourish).
  px(15, 0, 18, 2, SUIT);
  px(18, -0, 12, 4, SUIT);
  px(18, 3, 12, 1, "#2a2a2a");
  return canvas;
}
